// Fixed Player.jsx with proper time tracking and seek handling
import {
  createEffect,
  createMemo,
  createResource,
  onCleanup,
  createSignal,
  onMount,
  Show,
  For,
} from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { useNavigate } from "@solidjs/router";
import { useLocation } from "@solidjs/router";
import {
  isBuffering,
  playListPassword,
  playListUrl,
  playListUsername,
  setIsBuffering,
  setPreviewToNotShow,
} from "../../../state";
import videoPlayer from "../../video";
import { ProgressBar } from "@lightningtv/solid-ui";
import LoadingIndicator from "../loading/loading";
import { Buffer } from "buffer";
import theme from "theme";
import styles from "../../styles";
import { dnsResolver } from "../../api/providers/dnsResolver";
import AudioTracks from "./components/AudioTracks";
import TextTracks from "./components/TextTracks";
import { debugCapture } from "../../utils/debugCapture";
import ContentBlock from "../../components/ContentBlock";
import * as entityProvider from "../../api/providers/entity";

const DescriptionStylesSecond = {
  ...theme.typography.body1,
  fontFamily: "Roboto",
  fontWeight: 400,
  lineHeight: 32,
  width: 450,
  maxLines: 2,
  contain: "width",
  fontSize: 28,
  color: '#fff'
};

const Player = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = createSignal<string | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = createSignal(false);
  const [videoDuration, setVideoDuration] = createSignal(0);
  const [currentTime, setCurrentTime] = createSignal(0);
  const [displayTime, setDisplayTime] = createSignal(0); // For UI display during seeking
  const [contentType, setContentType] = createSignal("");
  const [nameMovie, setNameMovie] = createSignal("");
  const [progress, setProgress] = createSignal(0);
  const [extencion, setExtencion] = createSignal("");
  const [storedSeekTime, setStoredSeekTime] = createSignal(0);
  const [day, setDay] = createSignal("");
  const [dateTime, setDateTime] = createSignal("");
  const [seekError, setSeekError] = createSignal("");
  const [isSeekingUI, setIsSeekingUI] = createSignal(false); // Track UI seeking state

  const [streamStarted, setStreamStarted] = createSignal(false);
  const [posterUrl, setPosterUrl] = createSignal("");
  const [movieRating, setMovieRating] = createSignal("");

  const [showDebugLogs, setShowDebugLogs] = createSignal(false);
  const [debugLogs, setDebugLogs] = createSignal<string[]>([...debugCapture.getLogs()]);
  const [debugScrollOffset, setDebugScrollOffset] = createSignal(0);
  let keySequenceBuffer = "";
  const DEBUG_LINES = 22;

  const [showAudioMenu, setShowAudioMenu] = createSignal(false);
  const [selectedAudioTrack, setSelectedAudioTrack] = createSignal(0); // 0 = Track 1 (enabled), -1 = None (disabled)
  const [focusedAudioOption, setFocusedAudioOption] = createSignal(0);
  const [aspectRatio, setAspectRatio] = createSignal("contain"); // contain, cover, fill, stretch


  const [audioTracks, setAudioTracks] = createSignal<any[]>([]);
  const [audioTracksLoaded, setAudioTracksLoaded] = createSignal(false);

  const [textTracks, setTextTracks] = createSignal<any[]>([]);
  const [textTracksLoaded, setTextTracksLoaded] = createSignal(false);
  const [showTextMenu, setShowTextMenu] = createSignal(false);
  const [selectedTextTrack, setSelectedTextTrack] = createSignal(-1); // -1 = Off
  const [focusedTextOption, setFocusedTextOption] = createSignal(-1);

  const aspectRatioOptions = [
    { id: "contain", label: "Normal (Fit)", icon: "https://i.ibb.co/B2TWhB5Y/next.png" },
    { id: "cover", label: "Fill (Crop)", icon: "https://i.ibb.co/CsXz1pFj/next.png" },
    { id: "fill", label: "Stretch", icon: "https://i.ibb.co/yFDgmx5x/next.png" },
  ];

  let audioMenuRefs = [];
  let ref1, ref2, ref3, ref4, subtitlesRef, audioRef, videoRef, debugRef;

  onMount(() => {
    const now = new Date();
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = dayNames[now.getDay()];
    const formattedDate = now.toLocaleDateString("en-GB");
    const formattedTime = now.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
    setDay(dayName);
    setDateTime(`${formattedDate} | ${formattedTime}`);
  });

  // Subscribe to the global debug capture (patched in index.tsx before any framework code).
  const unsubscribeLogs = debugCapture.subscribe(() => {
    setDebugLogs([...debugCapture.getLogs()]);
  });

  const DEBUG_SEQUENCE = "12345";
  const handleKeyDown = (e: KeyboardEvent) => {
    keySequenceBuffer += e.key;
    if (keySequenceBuffer.length > DEBUG_SEQUENCE.length) {
      keySequenceBuffer = keySequenceBuffer.slice(-DEBUG_SEQUENCE.length);
    }
    if (keySequenceBuffer === DEBUG_SEQUENCE) {
      setShowDebugLogs((prev) => !prev);
      setDebugScrollOffset(0);
      keySequenceBuffer = "";
    }
  };
  window.addEventListener("keydown", handleKeyDown);

  onCleanup(() => {
    unsubscribeLogs();
    window.removeEventListener("keydown", handleKeyDown);
  });

  const DescriptionSecond = (props) => (
    <Text {...props} style={DescriptionStylesSecond}>
      {props.children}
    </Text>
  );

  const location = useLocation();
  let updateInterval;
  const [controlsVisible, setControlsVisible] = createSignal(true);
  let controlsTimeout;

  const showControls = () => {
    setControlsVisible(true);
    resetControlsTimeout();
  };

  const hideControls = () => {
    setControlsVisible(false);
  };

  const resetControlsTimeout = () => {
    clearTimeout(controlsTimeout);
    controlsTimeout = setTimeout(hideControls, 6000);
  };

  createEffect(() => {
    const params = new URLSearchParams(location.search);
    const num = params.get("str");
    const type = params.get("type");
    const name = params.get("name") ? decodeURIComponent(params.get("name")!) : "";
    const exc = params.get("extc");
    const storedSeek = params.get("seek");
    const poster = params.get("poster") ? decodeURIComponent(params.get("poster")!) : "";
    const rating = params.get("rating") || "";

    if (num !== null) setCurrentIndex(num);
    if (type) setContentType(type);
    if (name) setNameMovie(name);
    if (exc) setExtencion(exc);
    if (storedSeek) setStoredSeekTime(Number(storedSeek) || 0);
    setPosterUrl(poster);
    setMovieRating(rating && rating !== "0" ? rating : "");
  });

  // Fetch movie details to show in the loading screen (same data as Entity screen)
  const [entityInfo] = createResource(
    () => {
      const id = currentIndex();
      const type = contentType();
      if (!id || type !== "movie") return null;
      return { type, id, category_id: null };
    },
    (params) => entityProvider.getInfo(params)
  );

  // Derived backdrop URL — prefers cached entity data (already loaded by Entity screen),
  // falls back to the poster URL passed in navigation params.
  const loadingBackdrop = createMemo(() => {
    const bd = entityInfo()?.backgroundImage?.();
    return (Array.isArray(bd) ? bd[0] : bd) || posterUrl() || "";
  });

  const buildStreamUrl = async (streamOfID, type, exce) => {
    let originalVideoUrl = `${playListUrl()}/${type}/${playListUsername()}/${playListPassword()}/${streamOfID}.${exce}`;
    let finalVideoUrl = originalVideoUrl;

    try {
      const dnsResult = await dnsResolver.transformUrl(originalVideoUrl);
      if (typeof dnsResult === 'object' && dnsResult.url) {
        finalVideoUrl = dnsResult.url;
      } else if (typeof dnsResult === 'string' && dnsResult !== originalVideoUrl) {
        finalVideoUrl = dnsResult;
      }
    } catch (dnsError) {
      console.warn("DNS transformation failed:", dnsError);
    }

    if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
      const streamUrlProxy = Buffer.from(finalVideoUrl, "utf-8").toString("base64");
      finalVideoUrl = localStorage.getItem("proxy_url") + streamUrlProxy;
    }

    return { originalUrl: originalVideoUrl, finalUrl: finalVideoUrl };
  };

  // Cleanup on unmount
  onCleanup(() => {
    if (updateInterval) {
      clearInterval(updateInterval);
    }
    clearTimeout(controlsTimeout);
  });

  createEffect(async () => {
    const streamOfID = currentIndex();
    const type = contentType();
    const exce = extencion();

    if (!streamOfID || !type || !exce) return;

    try {
      const { originalUrl, finalUrl } = await buildStreamUrl(streamOfID, type, exce);

      setIsBuffering(true);
      setPreviewToNotShow(false);
      videoPlayer.setFullScreen();
      await videoPlayer.playVideo(finalUrl, "background");

      setIsVideoPlaying(true);

      setTimeout(() => {
        loadAudioTracks();
        loadTextTracks();
      }, 2000);

      // Get duration first
      const getDuration = async () => {
        try {
          // Wait a bit for player to be fully ready
          await new Promise(resolve => setTimeout(resolve, 1000));
          const duration = videoPlayer.getDuration();
          console.log("📏 Video duration:", Math.floor(duration / 1000), "seconds");
          setVideoDuration(duration);
          return duration;
        } catch (error) {
          console.error("Error getting duration:", error);
          return 0;
        }
      };

      const duration = await getDuration();

      // Handle initial seek if provided
      const seekTo = storedSeekTime();
      if (seekTo > 0 && duration > 0) {
        console.log("Performing initial seek to:", Math.floor(seekTo / 1000), "seconds");
        // Keep loading indicator on during seek

        try {
          // Reset any accumulated seek state before doing initial seek
          accumulatedSeekTime = 0;
          isAccumulatingSeeks = false;
          setIsSeekingUI(false);

          // Do direct seek without UI logic
          const actualSeekTime = await videoPlayer.seek(seekTo);

          // Update all states
          setCurrentTime(actualSeekTime);
          setDisplayTime(actualSeekTime);
          setProgress(actualSeekTime / duration);

          // Set base for future UI seeks
          baseSeekTime = actualSeekTime;

          // NOW turn off loading after seek completes
          setIsBuffering(false);
          setControlsVisible(true);
          setStreamStarted(true);
          resetControlsTimeout();

          console.log("Initial seek completed to:", Math.floor(actualSeekTime / 1000), "seconds");
        } catch (error) {
          console.warn("Initial seek failed:", error);
          setSeekError("Could not skip to saved position");
          setTimeout(() => setSeekError(""), 3000);
          // Turn off loading even if seek fails
          setIsBuffering(false);
          setControlsVisible(true);
          setStreamStarted(true);
          resetControlsTimeout();
        }
      } else {
        // No seek needed, turn off loading immediately
        setIsBuffering(false);
        setControlsVisible(true);
        setStreamStarted(true);
        resetControlsTimeout();
      }

      // Start time monitoring with proper state checking
      const timeUpdateHandler = () => {
        if (!location.pathname.startsWith("/player")) return;

        try {
          const playerState = videoPlayer.getState();

          // Only update time if player is ready and not seeking
          if (playerState.ready && !playerState.seeking && playerState.state === "PLAYING") {
            const newTime = videoPlayer.getCurrentTime();

            // Only update if this is a significant change and we're not in UI seeking mode
            if (!isSeekingUI() && !isAccumulatingSeeks) {
              const timeDiff = Math.abs(newTime - currentTime());

              // Update if time changed significantly or if it's been a while
              if (timeDiff > 100) { // More than 100ms difference
                setCurrentTime(newTime);
                setDisplayTime(newTime);

                // Update base seek time if we're not actively seeking
                if (!isAccumulatingSeeks) {
                  baseSeekTime = newTime;
                }
              }
            } else if (!isSeekingUI() && !isAccumulatingSeeks) {
              // Update current time even during seeking for internal tracking
              setCurrentTime(newTime);
            }
          }
        } catch (error) {
          const e = error as Error;
          if (!e.message?.includes("not ready") && !e.message?.includes("INVALID_STATE")) {
            console.warn("Time update error:", e.message);
          }
        }
      };

      // Start with faster initial updates, then slow down
      updateInterval = setInterval(timeUpdateHandler, 500);

      // After 10 seconds, reduce frequency to save resources
      setTimeout(() => {
        if (updateInterval) {
          clearInterval(updateInterval);
          updateInterval = setInterval(timeUpdateHandler, 1000);
        }
      }, 10000);

    } catch (error) {
      console.error("❌ Error in video setup:", error);
      setIsVideoPlaying(false);
      setIsBuffering(false);
      setSeekError("Video playback failed");
      setTimeout(() => setSeekError(""), 5000);
    }
  });

  const onCustomEscape = async () => {
    try {
      if (updateInterval) {
        clearInterval(updateInterval);
        updateInterval = null;
      }

      const actualTime = currentTime();
      const streamOfID = currentIndex();
      const type = contentType();
      const exce = extencion();

      if (streamOfID && type && exce && actualTime > 0) {
        const { originalUrl } = await buildStreamUrl(streamOfID, type, exce);
        const duration = videoDuration();

        if (actualTime < duration - 30000) { // Save if more than 30s left
          localStorage.setItem("vod_resume", JSON.stringify({
            videoUrl: originalUrl,
            currentTime: actualTime
          }));
          console.log("💾 Resume position saved:", Math.floor(actualTime / 1000), "seconds");
        }
      }
    } catch (error) {
      console.warn("Error saving resume data:", error);
    }

    videoPlayer.destroy();
    setPreviewToNotShow(true);

    setTimeout(() => {
      navigate(-1);
    }, 100);
  };

  const togglePlayPause = () => {
    try {
      const state = videoPlayer.getState();
      if (isVideoPlaying()) {
        console.log(`⏸ PAUSE — playerState=${state.state} ready=${state.ready} buffering=${state.buffering} time=${Math.floor(currentTime() / 1000)}s`);
        videoPlayer.pause();
        setIsVideoPlaying(false);
        console.log("⏸ Pause command sent");
      } else {
        console.log(`▶ PLAY — playerState=${state.state} ready=${state.ready} buffering=${state.buffering} time=${Math.floor(currentTime() / 1000)}s`);
        videoPlayer.resume();
        setIsVideoPlaying(true);
        console.log("▶ Resume command sent");
      }
      showControls();
    } catch (error) {
      console.error("Error toggling play/pause:", error);
      setSeekError("Playback control error");
      setTimeout(() => setSeekError(""), 2000);
    }
  };

  // Update progress based on current time changes
  createEffect(() => {
    const current = currentTime();
    const duration = videoDuration();

    if (!isFinite(current) || !isFinite(duration) || duration <= 0) return;

    const progressValue = Math.max(0, Math.min(current / duration, 1));
    setProgress(progressValue);
  });

  // Fixed seek implementation with proper state management
  let seekDebounceTimeout;
  let accumulatedSeekTime = 0;
  let baseSeekTime = 0;
  let isAccumulatingSeeks = false;

  const seekTime = (seconds) => {
    try {
      if (!videoPlayer.isReady()) {
        setSeekError("Player not ready");
        setTimeout(() => setSeekError(""), 2000);
        return;
      }

      const current = currentTime();
      const duration = videoDuration();

      if (!isFinite(current) || !isFinite(duration) || duration <= 0) {
        console.warn("Invalid time values for seeking");
        return;
      }

      // If this is the first seek in a sequence, set the base time
      if (!isAccumulatingSeeks) {
        baseSeekTime = current;
        accumulatedSeekTime = 0;
        isAccumulatingSeeks = true;
        setIsSeekingUI(true);
      }

      // Accumulate seek operations
      accumulatedSeekTime += seconds * 1000;

      // Calculate target position from the base time
      const targetTime = baseSeekTime + accumulatedSeekTime;
      const clampedTime = Math.max(0, Math.min(targetTime, duration - 2000));

      // Update UI immediately
      setDisplayTime(clampedTime);
      setProgress(clampedTime / duration);

      console.log(`🎯 Seeking: base=${Math.floor(baseSeekTime / 1000)}s + accumulated=${Math.floor(accumulatedSeekTime / 1000)}s = target=${Math.floor(clampedTime / 1000)}s`);

      // Clear previous timeout
      clearTimeout(seekDebounceTimeout);

      // Debounce the actual seek
      seekDebounceTimeout = setTimeout(async () => {
        try {
          console.log("🚀 Executing accumulated seek to:", Math.floor(clampedTime / 1000), "seconds");

          const actualSeekTime = await videoPlayer.seek(clampedTime);

          // Update with actual result and reset accumulation
          setCurrentTime(actualSeekTime);
          setDisplayTime(actualSeekTime);
          setProgress(actualSeekTime / duration);

          // Reset seek accumulation
          accumulatedSeekTime = 0;
          baseSeekTime = actualSeekTime;
          isAccumulatingSeeks = false;
          setIsSeekingUI(false);

          console.log("✅ Accumulated seek completed at:", Math.floor(actualSeekTime / 1000), "seconds");
          setSeekError("");

        } catch (error) {
          console.error("❌ Accumulated seek failed:", error);

          // Reset accumulation and try to get current time
          accumulatedSeekTime = 0;
          isAccumulatingSeeks = false;
          setIsSeekingUI(false);

          // Try to recover current position
          setTimeout(() => {
            try {
              const recoveredTime = videoPlayer.getCurrentTime();
              setCurrentTime(recoveredTime);
              setDisplayTime(recoveredTime);
              setProgress(recoveredTime / duration);
              baseSeekTime = recoveredTime;
              console.log("🔄 Recovered to time:", Math.floor(recoveredTime / 1000), "seconds");
            } catch (recoveryError) {
              console.warn("Could not recover time position");
            }
          }, 500);

          setSeekError("Seek failed - recovered");
          setTimeout(() => setSeekError(""), 2000);
        }
      }, 700); // Reduced debounce time

      showControls();

    } catch (error) {
      console.error("Error in seekTime:", error);
      accumulatedSeekTime = 0;
      isAccumulatingSeeks = false;
      setIsSeekingUI(false);
      setSeekError("Seek error");
      setTimeout(() => setSeekError(""), 2000);
    }
  };

  const onKeyPressActions = () => {
    showControls();
    resetControlsTimeout();
  };

  function formatTime(ms) {
    if (!isFinite(ms) || ms < 0) return "00:00:00";

    let totalSeconds = Math.floor(ms / 1000);
    let hours = Math.floor(totalSeconds / 3600);
    let minutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  const loadAudioTracks = () => {
    try {
      const tracks = videoPlayer.getAudioTracks();

      if (tracks && tracks.length > 0) {
        const formattedTracks = tracks.map((track, index) => ({
          id: index,
          label: track.label || track.language || `Track ${index + 1}`,
          language: track.language,
          enabled: track.enabled,
          icon: "🔊"
        }));

        const allTracks = [
          { id: -1, label: "None", icon: "🔇" },
          ...formattedTracks
        ];

        setAudioTracks(allTracks);
        setAudioTracksLoaded(true);

        const enabledIndex = tracks.findIndex(t => t.enabled);
        setSelectedAudioTrack(enabledIndex >= 0 ? enabledIndex : 0);
      } else {
        setAudioTracks([
          { id: -1, label: "None", icon: "🔇" },
          { id: 0, label: "Track 1", icon: "🔊" }
        ]);
        setAudioTracksLoaded(true);
      }
    } catch (error) {
      console.error("Error loading audio tracks:", error);
      setAudioTracks([
        { id: -1, label: "None", icon: "🔇" },
        { id: 0, label: "Track 1", icon: "🔊" }
      ]);
      setAudioTracksLoaded(true);
    }
  };

  const loadTextTracks = () => {
    try {
      const tracks = videoPlayer.getTextTracks();

      if (tracks && tracks.length > 0) {
        const formattedTracks = tracks.map((track, index) => ({
          id: index,
          label: track.label || track.language || `Subtitle ${index + 1}`,
          language: track.language,
          kind: track.kind,
          mode: track.mode,
          icon: "📝"
        }));

        const allTracks = [
          { id: -1, label: "Off", icon: "🚫" },
          ...formattedTracks
        ];

        setTextTracks(allTracks);
        setTextTracksLoaded(true);

        const showingIndex = tracks.findIndex(t => t.mode === 'showing');
        setSelectedTextTrack(showingIndex >= 0 ? showingIndex : -1);
      } else {
        setTextTracks([]);
        setTextTracksLoaded(true);
      }
    } catch (error) {
      console.error("Error loading text tracks:", error);
      setTextTracks([]);
      setTextTracksLoaded(true);
    }
  };

  const toggleTextMenu = () => {
    if (!showTextMenu()) {
      loadTextTracks();
    }
    setShowTextMenu(!showTextMenu());
    showControls();
    resetControlsTimeout();
  };

  const toggleAudioMenu = () => {
    if (!showAudioMenu()) {
      loadAudioTracks();
    }
    setShowAudioMenu(!showAudioMenu());
    showControls();
    resetControlsTimeout();
  };

  const toggleAscpectRatio = () => {
    const currentIndex = aspectRatioOptions.findIndex(opt => opt.id === aspectRatio());
    const nextIndex = (currentIndex + 1) % aspectRatioOptions.length;
    const nextRatio = aspectRatioOptions[nextIndex].id;

    setAspectRatio(nextRatio);
    applyAspectRatio(nextRatio);
    showControls();
    resetControlsTimeout();
  };

  const applyAspectRatio = (ratio) => {
    const videoElement = document.querySelector('video');

    if (videoElement) {
      videoElement.style.objectFit = ratio;

      setSeekError(`Aspect Ratio: ${aspectRatioOptions.find(opt => opt.id === ratio)?.label}`);
      setTimeout(() => setSeekError(""), 2000);
    }
  };

  return (
    <View>
      <View
        onEscape={() => {
          onCustomEscape();
        }}
        onUp={() => {
          onKeyPressActions();
        }}
        onDown={() => {
          onKeyPressActions();
        }}
        onBack={() => {
          hideControls();
          return true;
        }}
        autofocus
      >
        {/* Netflix-style info panel — visible until stream starts playing */}
        <Show when={!streamStarted()}>
          {/* Backdrop image — same pattern as Entity screen */}
          <View src={loadingBackdrop()}>
            {/* Hero gradient mask (same overlay used on Entity screen) */}
            <View src={"https://i.ibb.co/NrtvBBD/hero-mask-inverted.png"}>
              {/* Transparent when backdrop exists, dark when not — same opacity logic as Entity */}
              <View color={loadingBackdrop() ? hexColor("#00000000") : hexColor("#0d0f14")}>

                {/* ContentBlock with full movie details (genre, title, stars, description, cast…) */}
                <Show when={entityInfo()?.heroContent} fallback={
                  <Text
                    x={170} y={300}
                    fontSize={68}
                    fontWeight="bold"
                    color={hexColor("#ffffff")}
                    width={1000}
                    contain="width"
                    maxLines={2}
                  >
                    {nameMovie()}
                  </Text>
                }>
                  <ContentBlock
                    y={200}
                    x={170}
                    content={entityInfo().heroContent}
                  />
                </Show>

                {/* Loading spinner at bottom-center */}
                <View x={800} y={650}>
                  <LoadingIndicator />
                </View>

              </View>
            </View>
          </View>
        </Show>

        {/* Mid-playback buffering spinner */}
        <Show when={isBuffering() && streamStarted()}>
          <View x={850} y={400}>
            <LoadingIndicator />
          </View>
        </Show>

        {/* Show seek error if any */}
        <Show when={seekError()}>
          <View
            color={hexColor("#000000cc")}
            borderRadius={8}
            zIndex={1000}>
            <Text fontSize={24} color="white" textAlign="center">
              {seekError()}
            </Text>
          </View>
        </Show>

        {/* Show seeking indicator with accumulated time */}
        {/* <Show when={isSeekingUI()}>
          <View x={960} y={250} color={hexColor("#000000CC")}
            borderRadius={8}
            zIndex={1000}>
            <Text fontSize={24} color="white" textAlign="center">
              Seeking... {formatTime(displayTime())}
              {accumulatedSeekTime !== 0 && (
                <Text fontSize={18} color="yellow" y={30}>
                  {accumulatedSeekTime > 0 ? '+' : ''}{Math.floor(accumulatedSeekTime / 1000)}s
                </Text>
              )}
            </Text>
          </View>
        </Show> */}

        <Show when={controlsVisible() && streamStarted()}>
          <View
            src={"https://i.ibb.co/sHw6rPz/Pngtree-black-gradient-vertical-gradient-element-5441580.png"}
          ></View>
          <View
            src={
              "https://i.ibb.co/NSvf1HZ/Lovepik-com-380148632-black-transparent-vertical-linear-gradient-gradient-element-transparency-black.png"
            }
          ></View>
          <View y={50} x={1500}>
            <Text fontWeight={'bold'} fontSize={32}>{day()}</Text>
            <Text fontSize={26} color={"#a0a1a3"} y={40}>{dateTime()}</Text>
          </View>
          <DescriptionSecond y={910} x={20}>{nameMovie()}</DescriptionSecond>
          <Text y={1020} fontSize={24} x={690}>
            {formatTime(displayTime())}
          </Text>
          <View
            y={967}
            x={400}
            ref={ref4}
            style={styles.progressBar}
            onUp={() => { if (showDebugLogs()) { debugRef.setFocus(); return true; } }}
            onDown={() => { ref2.setFocus() }}
            onLeft={() => {
              seekTime(-15);
              onKeyPressActions();
            }}
            onRight={() => {
              seekTime(15);
              onKeyPressActions();
            }}
          >
            <ProgressBar
              progress={progress()}
              borderRadius={5}
              height={10}
              width={1200}
              zIndex={99}
              value={0}
              y={2.5}
              progressRadius={100}
            />
          </View>
          <Text fontSize={24} y={1020} x={1220}>{formatTime(videoDuration())}</Text>

          <Show when={!isBuffering()}>
            <View x={100} y={100}>
              <View
                style={styles.playButton}
                x={800}
                y={900}
              >
                <View
                  ref={ref1}
                  onRight={() => { ref2.setFocus() }}
                  onUp={() => { ref4.setFocus() }}
                  onEnter={() => {
                    seekTime(-15);
                    onKeyPressActions();
                  }}
                  src={
                    "https://i.ibb.co/Ps5dsyBH/back.png"
                  }
                  x={9}
                  y={9}
                  width={40}
                  height={40}
                />
              </View>

              <View
                ref={ref2}
                onUp={() => { ref4.setFocus() }}
                onEnter={() => {
                  togglePlayPause();
                }}
                onLeft={() => { ref1.setFocus() }}
                onRight={() => { ref3.setFocus() }}
                autofocus={true}
                style={styles.playButton}
                x={isVideoPlaying() ? 870 : 870}
                y={isVideoPlaying() ? 900 : 900}
              >
                <View
                  src={
                    isVideoPlaying()
                      ? "https://i.ibb.co/LDXv0w9X/pause.png"
                      : "https://i.ibb.co/SDHcywjP/play-button.png"
                  }
                  x={isVideoPlaying() ? 5 : 16}
                  y={isVideoPlaying() ? 5 : 15}
                  width={isVideoPlaying() ? 50 : 30}
                  height={isVideoPlaying() ? 50 : 30}
                />
              </View>

              <View
                ref={ref3}
                onUp={() => { ref4.setFocus() }}
                onEnter={() => {
                  seekTime(15);
                  onKeyPressActions();
                }}
                onLeft={() => { ref2.setFocus() }}
                onRight={() => { textTracks().length > 0 ? subtitlesRef.setFocus() : audioRef.setFocus(); }}
                style={styles.playButton}
                x={940}
                y={900}
              >
                <View
                  src={
                    "https://i.ibb.co/BVqTZWDz/next.png"
                  }
                  x={9}
                  y={9}
                  width={40}
                  height={40}
                />
              </View>

              {/* <Show when={textTracks().length > 0}>
                <View
                  ref={subtitlesRef}
                  onUp={() => { ref4.setFocus() }}
                  onEnter={() => {
                    toggleTextMenu();
                  }}
                  onLeft={() => { ref3.setFocus() }}
                  onRight={() => { audioRef.setFocus() }}
                  style={styles.playButton}
                  x={1500}
                  y={900}
                >
                  <View
                    src={"https://i.ibb.co/8HvJqT2/next.png"}
                    x={9}
                    y={9}
                    width={40}
                    height={40}
                  />
                </View>
              </Show>

              <View
                ref={audioRef}
                onUp={() => { ref4.setFocus() }}
                onEnter={() => {
                  toggleAudioMenu();
                }}
                onLeft={() => { textTracks().length > 0 ? subtitlesRef.setFocus() : ref3.setFocus() }}
                onRight={() => { videoRef.setFocus() }}
                style={styles.playButton}
                x={1570}
                y={900}
              >
                <View
                  src={"https://i.ibb.co/svMNkFSH/next.png"}
                  x={9}
                  y={9}
                  width={40}
                  height={40}
                />
              </View>

              <View
                ref={videoRef}
                onUp={() => { ref4.setFocus() }}
                onEnter={() => {
                  toggleAscpectRatio();
                }}
                onLeft={() => { audioRef.setFocus() }}
                style={styles.playButton}
                x={1640}
                y={900}
              >
                <View
                  src={aspectRatioOptions.find(opt => opt.id === aspectRatio())?.icon}
                  x={9}
                  y={9}
                  width={40}
                  height={40}
                />
              </View> */}


              {/* <Show when={showTextMenu()}>
                <TextTracks
                  textTracks={textTracks()}
                  videoPlayer={videoPlayer}
                  subtitlesRef={subtitlesRef}
                  ref2={ref2}
                  setSelectedTextTrack={setSelectedTextTrack}
                  selectedTextTrack={selectedTextTrack}
                  setShowTextMenu={setShowTextMenu}
                  setFocusedTextOption={setFocusedTextOption}
                  focusedTextOption={focusedTextOption}
                  setSeekError={setSeekError}
                  showControls={showControls}
                />
              </Show>

              <Show when={showAudioMenu()}>
                <AudioTracks
                  audioTracks={audioTracks()}
                  videoPlayer={videoPlayer}
                  audioMenuRefs={audioMenuRefs}
                  audioRef={audioRef}
                  ref2={ref2}
                  setSelectedAudioTrack={setSelectedAudioTrack}
                  selectedAudioTrack={selectedAudioTrack}
                  setShowAudioMenu={setShowAudioMenu}
                  setFocusedAudioOption={setFocusedAudioOption}
                  focusedAudioOption={focusedAudioOption}
                  setSeekError={setSeekError}
                  showControls={showControls}
                />
              </Show> */}
            </View>
          </Show>
        </Show>
      </View>

      <Show when={showDebugLogs()}>
        <View
          ref={debugRef}
          x={20}
          y={20}
          width={680}
          height={440}
          color={hexColor("#000000e0")}
          borderRadius={8}
          zIndex={99999}
          onUp={() => { setDebugScrollOffset((p) => Math.max(0, p - 1)); }}
          onDown={() => {
            const max = Math.max(0, debugLogs().length - DEBUG_LINES);
            if (debugScrollOffset() >= max) {
              ref4.setFocus();
            } else {
              setDebugScrollOffset((p) => Math.min(max, p + 1));
            }
          }}
          onRight={() => { ref4.setFocus(); return true; }}
          onEscape={() => { ref4.setFocus(); return true; }}
          onBack={() => { ref4.setFocus(); return true; }}
        >
          {/* header */}
          <Text x={12} y={10} fontSize={14} fontWeight="bold" color={hexColor("#ffffff")}>
            DEBUG CONSOLE
          </Text>
          <Text x={480} y={10} fontSize={12} color={hexColor("#888888")}>
            {`${debugScrollOffset() + 1}-${Math.min(debugScrollOffset() + DEBUG_LINES, debugLogs().length)} / ${debugLogs().length}`}
          </Text>

          {/* log lines */}
          <For each={debugLogs().slice(debugScrollOffset(), debugScrollOffset() + DEBUG_LINES)}>
            {(log, i) => (
              <Text
                x={12}
                y={34 + i() * 18}
                fontSize={12}
                width={656}
                contain="width"
                color={hexColor(
                  log.includes("ERR") ? "#ff4444" :
                    log.includes("WARN") ? "#ffaa00" :
                      "#00ff66"
                )}
              >
                {log}
              </Text>
            )}
          </For>
        </View>
      </Show>
    </View>
  );
};

export default Player;